import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  IconButton,
  useDisclosure,
  Icon,
  Button,
} from "@chakra-ui/react";
import { FiPlus, FiMap, FiSend } from "react-icons/fi";
import {
  useRemixActivityMutation,
  useGoLiveMutation,
} from "@store/api/activities";
import {
  useProfile,
  useAgendaActions,
  useMeeting,
  useToastNotification,
  useDemoFlow,
  useInvite,
} from "@hooks";
import { isWebUser, isZoomApp } from "@utils/helpers";
import { hasDemoGuestToken, clearDemoGuestToken } from "@utils/guestDemo";
import { categorizeActivities } from "@utils/activityHelpers";
import { $Routes } from "@routes";
import { AgendaButton, AgendaModal, UserCount, Breadcrumb } from "@components";
import ActivityList from "./ActivityList";
import ActivityCompletionNotification from "../Activity/ActivityCompletionNotification";
import { removeFromGracePeriod } from "@store/activitySlice";
import { RocketIcon } from "./AccordionIcons";
import ActivitySelectionModal from "./ActivitySelectionModal";
import NoteCreationModal from "./NoteCreationModal";
import PollCreationModal from "./PollCreationModal";
import BlueprintModal from "./BlueprintModal";
import { RemixActivityModal } from "@components/Activity";
import RemixLimitModal from "../Modals/RemixLimitModal";
import { EPulse } from "@components/EPulse";
import ShareLinkModal from "./ShareLinkModal";
import ShareLinkButton from "./ShareLinkButton";
import {
  DemoModeBanner,
  DemoFlowModal,
  DemoGuidePopover,
  DemoNarrationBar,
  DemoActionCard,
  DemoResultsModal,
} from "@components/Demo";

const NewAgendaView = ({
  agenda: currentAgenda,
  activities,
  isLoading: isAgendaLoading,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { agendaId } = useParams();
  const { profile } = useProfile();

  // Get current agenda from Redux store to ensure real-time updates
  const reduxCurrentAgenda = useSelector(
    (state) => state.agendas.currentAgenda
  );

  // Get activities from Redux store to ensure real-time updates
  const reduxActivities = useSelector((state) => state.activity.activities);
  const gracePeriodActivities = useSelector(
    (state) => state.activity.gracePeriodActivities
  );

  // Use Redux store agenda if available (for real-time updates), otherwise fallback to prop
  const effectiveAgenda =
    reduxCurrentAgenda?._id === agendaId ? reduxCurrentAgenda : currentAgenda;
  const { goLiveAgenda, startWebMeeting, stopLiveAgenda, stopWebMeeting } =
    useAgendaActions();
  const {
    isMeetingActive,
    isHostOrCoHost,
    meetingInstanceId,
    meetingParticipants,
  } = useMeeting();
  const { showSuccessToast, showErrorToast } = useToastNotification();

  // Modal state for Activity Selection modal
  const {
    isOpen: isActivityModalOpen,
    onOpen: onActivityModalOpen,
    onClose: onActivityModalClose,
  } = useDisclosure();

  // EPulse minimized state
  const [isEPulseMinimized, setIsEPulseMinimized] = useState(false);
  const hasAutoMinimizedEPulseRef = useRef(false);

  // Edit modal state
  const [editingActivity, setEditingActivity] = useState(null);
  const [editModalType, setEditModalType] = useState(null); // 'note' or 'poll'
  const showBlueprintIcon = false;

  // Blueprint modal state
  const {
    isOpen: isBlueprintModalOpen,
    onOpen: onBlueprintModalOpen,
    onClose: onBlueprintModalClose,
  } = useDisclosure();

  // Remix modal state
  const [isRemixModalOpen, setIsRemixModalOpen] = useState(false);
  const [isRemixLimitModalOpen, setIsRemixLimitModalOpen] = useState(false);
  const [activityToRemix, setActivityToRemix] = useState(null);
  const [remixActivity] = useRemixActivityMutation();

  // Share link modal state
  const {
    isOpen: isShareModalOpen,
    onOpen: onShareModalOpen,
    onClose: onShareModalClose,
  } = useDisclosure();

  const [canGoLive, setCanGoLive] = useState(false);
  const [stopLiveAgendaModal, setStopLiveAgendaModal] = useState(false);
  const [startAgendaModal, setStartAgendaModal] = useState(false);

  // Data is already fetched by AgendaDetailView parent component
  // No need to fetch again here to avoid infinite loops
  const fetchingAgenda = false;

  // Use Redux activities if available, otherwise fallback to prop
  const effectiveActivities =
    reduxActivities?.length > 0 ? reduxActivities : activities;

  // Calculate pending activities count (activities shown in Activity Schedule)
  // Use the exact same categorizeActivities logic to get pending count
  const unrunActivitiesCount = effectiveActivities
    ? categorizeActivities(effectiveActivities, !!effectiveAgenda?.endTime)
        .pending.length
    : 0;

  // Demo mode detection - check agenda flag or URL param
  const isDemoMode = useMemo(() => {
    const demoParam = new URLSearchParams(location.search).get("demo");
    return effectiveAgenda?.isDemo || demoParam === "true";
  }, [effectiveAgenda?.isDemo, location.search]);

  // Demo flow hook (replaces useDemoTour)
  const {
    isActive: isDemoActive,
    currentStep: demoCurrentStep,
    stepConfig: demoStepConfig,
    firstActivity: demoFirstActivity,
    secondActivity: demoSecondActivity,
    thirdActivity: demoThirdActivity,
    fourthActivity: demoFourthActivity,
    initDemo,
    advance: advanceDemoStep,
    stopDemo,
    triggerEmojiWave,
  } = useDemoFlow({
    agenda: effectiveAgenda,
    activities: effectiveActivities,
    isShareModalOpen,
    participantCount: meetingParticipants.length,
  });

  // The ENGAGEMENT_SIGNAL step explains the chart while it is expanded; once the
  // demo moves on, collapse it once to free vertical space for the activity
  // steps. One-shot on purpose — if the user reopens it, leave it alone.
  useEffect(() => {
    if (!isDemoActive || demoCurrentStep !== "PLAY_ACTIVITY") return;
    if (hasAutoMinimizedEPulseRef.current) return;

    hasAutoMinimizedEPulseRef.current = true;
    setIsEPulseMinimized(true);
  }, [isDemoActive, demoCurrentStep]);

  // Zoom App invite hook
  const { handleSendZoomInvite } = useInvite();

  // Handle invite button click (Zoom App demo flow)
  const handleInviteClick = useCallback(async () => {
    if (isDemoActive && demoCurrentStep === "INVITE_TO_APP") {
      // During demo, skip the real Zoom SDK invite and just advance the step
      advanceDemoStep();
      return;
    }
    await handleSendZoomInvite();
  }, [handleSendZoomInvite, isDemoActive, demoCurrentStep, advanceDemoStep]);

  // Activity go-live mutation for demo PLAY_ACTIVITY action
  const [goLiveActivity] = useGoLiveMutation();

  // State to track if demo has been initialized
  const [demoInitialized, setDemoInitialized] = useState(() => {
    if (typeof window !== "undefined" && agendaId) {
      const demoStartedKey = `demo_started_${agendaId}`;
      return localStorage.getItem(demoStartedKey) === "true";
    }
    return false;
  });

  // Initialize demo flow on mount when in demo mode
  useEffect(() => {
    if (
      isDemoMode &&
      !demoInitialized &&
      !isAgendaLoading &&
      effectiveAgenda &&
      !effectiveAgenda.live // Don't show welcome if agenda is already running
    ) {
      const timer = setTimeout(() => {
        initDemo(agendaId);
        setDemoInitialized(true);
        if (agendaId) {
          localStorage.setItem(`demo_started_${agendaId}`, "true");
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [
    isDemoMode,
    demoInitialized,
    isAgendaLoading,
    effectiveAgenda,
    initDemo,
    agendaId,
  ]);

  const { handleGoLive, isLoading: isGoingLive } = goLiveAgenda;
  const { handleStartWebMeeting, isLoading: isStartingWebMeeting } =
    startWebMeeting;
  const { handleStopLive, isLoading: isStoppingLive } = stopLiveAgenda;
  const { handleStopWebMeeting, isLoading: isStoppingWebMeeting } =
    stopWebMeeting;

  const stopLive = useCallback(async () => {
    if (effectiveAgenda?.live) {
      if (isDemoMode || isWebUser(profile)) {
        // During demo mode, always use web meeting stop (even on Zoom Apps)
        handleStopWebMeeting(agendaId, () => {
          setStopLiveAgendaModal(false);
        });
      } else {
        handleStopLive({
          agendaId,
          onSuccess: () => {
            setStopLiveAgendaModal(false);
          },
        });
      }
    }
  }, [
    agendaId,
    effectiveAgenda?.live,
    handleStopLive,
    handleStopWebMeeting,
    isDemoMode,
    profile,
  ]);

  const handleEndDemo = useCallback(() => {
    stopDemo();
    const isGuestDemo = hasDemoGuestToken();
    const goToNextPage = () => {
      if (isGuestDemo) {
        clearDemoGuestToken();
        navigate($Routes.trySmartAgendaPage);
      } else {
        navigate($Routes.smartAgendasPage);
      }
    };
    if (effectiveAgenda?.live) {
      if (isDemoMode || isWebUser(profile)) {
        // During demo mode, always use web meeting stop (even on Zoom Apps)
        handleStopWebMeeting(agendaId, goToNextPage, { isDemoMode });
      } else {
        handleStopLive({ agendaId, onSuccess: goToNextPage });
      }
    } else {
      goToNextPage();
    }
  }, [
    stopDemo,
    effectiveAgenda?.live,
    agendaId,
    handleStopWebMeeting,
    handleStopLive,
    isDemoMode,
    profile,
    navigate,
  ]);

  const startAgenda = useCallback(async () => {
    if (isDemoMode || isWebUser(profile)) {
      // During demo mode, always use web meeting (even on Zoom Apps)
      handleStartWebMeeting(agendaId, { isDemoMode });
    } else {
      handleGoLive({
        agendaId,
        meetingInstanceId,
        onError: (error) => {
          console.error("Error going live:", error);
        },
      });
    }
    setStartAgendaModal(false);
  }, [
    agendaId,
    handleGoLive,
    handleStartWebMeeting,
    isDemoMode,
    meetingInstanceId,
    profile,
  ]);

  // Handle demo flow button actions
  const handleDemoAction = useCallback(
    async (actionId) => {
      switch (actionId) {
        case "START_AGENDA":
          // Start the agenda (same as clicking the real Start Agenda button)
          startAgenda();
          break;

        case "PLAY_ACTIVITY": {
          // Find the first activity and go live
          const firstActivity =
            effectiveActivities?.find((a) => a.order === 0) ||
            effectiveActivities?.[0];
          if (firstActivity) {
            try {
              await goLiveActivity(firstActivity._id).unwrap();
            } catch (error) {
              console.error("Error starting demo activity:", error);
            }
          }
          break;
        }

        case "CONTINUE":
          // Trigger emoji wave 2, then advance
          if (triggerEmojiWave) {
            triggerEmojiWave({
              meetingInstanceId,
              waveType: "wave2",
            });
          }
          advanceDemoStep();
          break;

        default:
          break;
      }
    },
    [
      startAgenda,
      effectiveActivities,
      goLiveActivity,
      advanceDemoStep,
      triggerEmojiWave,
      meetingInstanceId,
    ]
  );

  const handleAgendaActionButtonClick = useCallback(async () => {
    if (effectiveAgenda?.live) {
      // Show confirmation modal instead of directly stopping
      setStopLiveAgendaModal(true);
    } else {
      if (canGoLive) {
        if (isDemoMode) {
          // Skip confirmation modal in demo mode
          startAgenda();
        } else {
          setStartAgendaModal(true);
        }
      }
    }
  }, [canGoLive, effectiveAgenda?.live, isDemoMode, startAgenda]);

  const agendaButtonDisabled = useMemo(() => {
    if (isDemoMode) {
      // During demo mode, only disable if agenda has ended
      return !!effectiveAgenda?.endTime;
    }

    if (isWebUser(profile)) {
      return !canGoLive || !!effectiveAgenda?.endTime;
    }

    const isAgendaDisabled =
      effectiveAgenda?.endTime || (!isMeetingActive && !effectiveAgenda?.live);

    return isAgendaDisabled || !isHostOrCoHost(profile?._id);
  }, [
    effectiveAgenda?.endTime,
    effectiveAgenda?.live,
    isDemoMode,
    isMeetingActive,
    isHostOrCoHost,
    profile,
    canGoLive,
  ]);

  const agendaButtonDisabledText = useMemo(() => {
    if (isDemoMode) {
      // During demo mode, no disabled text (button is always enabled unless agenda ended)
      if (effectiveAgenda?.endTime) return "This agenda has ended";
      return null;
    }

    if (isWebUser(profile)) {
      if (effectiveAgenda?.endTime) return "This agenda has ended";
      if (effectiveAgenda?.userId?.toString() !== profile?._id?.toString())
        return "Only the agenda owner can start this meeting";
      return null;
    }

    if (!isMeetingActive) return "Start a Zoom meeting to run this Agenda";
    if (!isHostOrCoHost(profile?._id))
      return "You must be host or co-host to go live";
    if (effectiveAgenda?.endTime) return "This agenda has ended";
    return null;
  }, [effectiveAgenda, isDemoMode, isMeetingActive, isHostOrCoHost, profile]);

  useEffect(() => {
    if (!effectiveAgenda) return;

    if (isDemoMode) {
      // During demo mode, allow go-live as long as agenda hasn't ended
      setCanGoLive(!effectiveAgenda.endTime && !effectiveAgenda.live);
      return;
    }

    if (isWebUser(profile)) {
      if (
        effectiveAgenda &&
        effectiveAgenda.userId?.toString() === profile?._id?.toString()
      ) {
        setCanGoLive(true);
      } else {
        setCanGoLive(false);
      }
    } else {
      if (
        effectiveAgenda &&
        !effectiveAgenda.live &&
        isMeetingActive &&
        isHostOrCoHost(profile?._id)
      ) {
        setCanGoLive(true);
      } else {
        setCanGoLive(false);
      }
    }
  }, [effectiveAgenda, isDemoMode, isMeetingActive, isHostOrCoHost, profile]);

  // Handle activity selection from modal
  const handleAddActivity = useCallback((activityType) => {
    // The ActivitySelectionModal now handles navigation itself,
    // so we don't need to navigate here anymore
  }, []);

  // Handle edit activity requests from ActivityCardContainer
  const handleEditActivity = useCallback((activity) => {
    setEditingActivity(activity);

    // Determine which modal to open based on activity type
    if (activity.type === "poll" || activity.type === "quiz") {
      setEditModalType("poll");
    } else if (
      activity.type === "note" ||
      activity.type === "link" ||
      activity.type === "share"
    ) {
      setEditModalType("note");
    }
  }, []);

  // Handle saving updates to activities (this will be called by modals in edit mode)
  const handleEditSave = useCallback((values) => {
    // Close the modal after successful save (error handling is done in the modal)
    setEditingActivity(null);
    setEditModalType(null);
  }, []);

  // Handle closing edit modals
  const handleCloseEditModal = useCallback(() => {
    setEditingActivity(null);
    setEditModalType(null);
  }, []);

  // Handle initiating remix from edit modals
  const handleInitiateRemix = useCallback((activity) => {
    setActivityToRemix(activity);
    setEditModalType(null); // Close edit modal
    setEditingActivity(null);
    setIsRemixModalOpen(true); // Open remix modal
  }, []);

  // Handle remix modal close
  const handleRemixClose = useCallback(() => {
    setIsRemixModalOpen(false);
    setActivityToRemix(null);
  }, []);

  // Handle remix completion
  const handleRemixComplete = useCallback(
    async (remixData) => {
      if (remixData && remixData.activity && activityToRemix) {
        try {
          // Extract the needed data for the API call
          const { activity: activityFromModal, ...inputs } = remixData;

          const payload = {
            agendaId: effectiveAgenda._id,
            hostRefinements: inputs.hostRefinements || "",
            tone: inputs.tone || [],
            demographics: inputs.demographics || [],
            audience: inputs.audience || [],
            topics: inputs.topics || [],
            goals: inputs.goals || [],
            keepActivityType: inputs.keepActivityType || false,
          };

          await remixActivity({
            activityId: activityToRemix._id,
            body: payload,
          }).unwrap();

          showSuccessToast("Activity remixed successfully!");
          setIsRemixModalOpen(false);
          setActivityToRemix(null);
        } catch (error) {
          console.error("Remix failed:", error);

          // Check if it's a limit exceeded error (403 status)
          if (
            error?.status === 403 ||
            error?.data?.message?.includes("limit exceeded")
          ) {
            setIsRemixModalOpen(false);
            setIsRemixLimitModalOpen(true);
            return;
          }

          showErrorToast("Failed to remix activity. Please try again.");
        }
      }
    },
    [
      remixActivity,
      activityToRemix,
      effectiveAgenda,
      showSuccessToast,
      showErrorToast,
    ]
  );

  // Demo: advance from COPY_LINK when user copies the link
  const handleShareModalCopy = useCallback(() => {
    if (isDemoActive && demoCurrentStep === "COPY_LINK") {
      advanceDemoStep();
    }
  }, [isDemoActive, demoCurrentStep, advanceDemoStep]);

  // Demo: auto-close ShareLinkModal when step advances past COPY_LINK
  useEffect(() => {
    if (!isDemoActive || !isShareModalOpen) return;
    if (demoCurrentStep !== "SHARE_LINK" && demoCurrentStep !== "COPY_LINK") {
      // Step has moved past the share modal steps — close modal after brief delay for toast
      const timerId = setTimeout(() => {
        onShareModalClose();
      }, 500);
      return () => clearTimeout(timerId);
    }
  }, [isDemoActive, demoCurrentStep, isShareModalOpen, onShareModalClose]);

  // Find activity in grace period for notification
  const activityInGracePeriod = useMemo(() => {
    if (gracePeriodActivities.length === 0 || !effectiveActivities) return null;
    // Get the first activity in grace period (could be extended to show multiple)
    const activityId = gracePeriodActivities[0];
    return (
      effectiveActivities.find((activity) => activity._id === activityId) ||
      null
    );
  }, [gracePeriodActivities, effectiveActivities]);

  // Notification handlers
  const handleMoveNow = useCallback(
    (activityId) => {
      dispatch(removeFromGracePeriod({ activityId }));
    },
    [dispatch]
  );

  const handleDismissNotification = useCallback(
    (activityId) => {
      dispatch(removeFromGracePeriod({ activityId }));
    },
    [dispatch]
  );

  return (
    <>
      {/* Demo Flow Modal (center-positioned steps) */}
      <DemoFlowModal
        isOpen={isDemoActive && demoStepConfig?.modalType === "center"}
        stepConfig={demoStepConfig}
        onAction={handleDemoAction}
        onEndDemo={handleEndDemo}
      />

      {/* Demo Guide Popover (for popover steps like WELCOME and GUIDE_STOP) */}
      <DemoGuidePopover
        isOpen={isDemoActive && demoStepConfig?.modalType === "popover"}
        stepConfig={demoStepConfig}
        onEndDemo={handleEndDemo}
      />

      {/* Demo Results Modal (shows actual poll/quiz results after activity ends) */}
      <DemoResultsModal
        isOpen={isDemoActive && demoStepConfig?.modalType === "results_modal"}
        stepConfig={demoStepConfig}
        activity={
          demoCurrentStep === "SECOND_RESULTS_MODAL"
            ? demoSecondActivity
            : demoFirstActivity
        }
        onAction={handleDemoAction}
        onEndDemo={handleEndDemo}
      />

      {/* Demo Narration Bar (top bar for "watch this" steps) */}
      <DemoNarrationBar
        isOpen={isDemoActive && demoStepConfig?.modalType === "narration_bar"}
        stepConfig={demoStepConfig}
        onAction={handleDemoAction}
        onEndDemo={handleEndDemo}
      />

      {/* Demo Action Card (bottom card for "do this" steps) */}
      <DemoActionCard
        isOpen={isDemoActive && demoStepConfig?.modalType === "action_card"}
        stepConfig={demoStepConfig}
        onAction={handleDemoAction}
        onEndDemo={handleEndDemo}
      />

      <AgendaModal
        title="End Smart Agenda"
        body="Are you sure you want to end this smart agenda?"
        isOpen={stopLiveAgendaModal}
        isLoading={isStoppingLive || isStoppingWebMeeting}
        onClose={() => setStopLiveAgendaModal(false)}
        handlePrimaryAction={stopLive}
        primaryButtonText="End"
        primaryButtonVariant="error"
        handleSecondaryAction={() => setStopLiveAgendaModal(false)}
      />

      <AgendaModal
        title="Ready to Start Your Agenda?"
        body="You're about to begin tracking real-time engagement! Your participants will be able to interact and share feedback throughout the meeting."
        isOpen={startAgendaModal}
        isLoading={isGoingLive || isStartingWebMeeting}
        onClose={() => setStartAgendaModal(false)}
        handlePrimaryAction={startAgenda}
        primaryButtonText="Start Agenda"
        primaryButtonVariant="primary"
        handleSecondaryAction={() => setStartAgendaModal(false)}
      />

      <ActivitySelectionModal
        isOpen={isActivityModalOpen}
        onClose={onActivityModalClose}
        onAddActivity={handleAddActivity}
      />

      {/* Edit modals */}
      <NoteCreationModal
        isOpen={editModalType === "note"}
        onClose={handleCloseEditModal}
        activityType={editingActivity?.type}
        onSave={handleEditSave}
        initialData={editingActivity}
        onInitiateRemix={handleInitiateRemix}
      />

      <PollCreationModal
        isOpen={editModalType === "poll"}
        onClose={handleCloseEditModal}
        activityType={editingActivity?.type}
        onSave={handleEditSave}
        initialData={editingActivity}
        onInitiateRemix={handleInitiateRemix}
      />

      {/* Blueprint Modal */}
      <BlueprintModal
        isOpen={isBlueprintModalOpen}
        onClose={onBlueprintModalClose}
        blueprint={effectiveAgenda?.blueprint}
        onRemix={(blueprint) => {
          console.log("Remix blueprint:", blueprint);
          onBlueprintModalClose();
        }}
        isPastAgenda={!!effectiveAgenda?.endTime}
      />

      {/* Remix Activity Modal */}
      <RemixActivityModal
        isOpen={isRemixModalOpen}
        onClose={handleRemixClose}
        onRemixComplete={handleRemixComplete}
        activity={activityToRemix}
        agenda={effectiveAgenda}
      />

      {/* Remix Limit Modal */}
      <RemixLimitModal
        isOpen={isRemixLimitModalOpen}
        onClose={() => setIsRemixLimitModalOpen(false)}
      />

      {/* Share Link Modal */}
      <ShareLinkModal
        isOpen={isShareModalOpen}
        onClose={onShareModalClose}
        agenda={effectiveAgenda}
        onCopy={handleShareModalCopy}
      />

      <Box
        bg="gray.25"
        position="relative"
        height="100%"
        display="flex"
        flexDirection="column"
      >
        {/* Demo Mode Banner */}
        {isDemoMode && <DemoModeBanner />}

        {/* Header */}
        <Flex
          bg="white"
          align="center"
          justify="space-between"
          borderBottom="1px solid"
          borderColor="gray.200"
          flexShrink={0}
          p={2}
        >
          <Breadcrumb
            items={[
              {
                title: "Agenda",
                onClick: () => navigate($Routes.allAgendaPage),
                testId: "agenda-header-back",
              },
              {
                title: effectiveAgenda?.title
                  ? effectiveAgenda.title.length > 5
                    ? `${effectiveAgenda.title.substring(0, 8)}...`
                    : effectiveAgenda.title
                  : "Loading...",
                testId: "agenda-title",
              },
            ]}
          />

          <HStack spacing={2}>
            {(isMeetingActive || (isDemoActive && effectiveAgenda?.live)) && (
              <UserCount count={meetingParticipants.length} />
            )}
            <AgendaButton
              agendaState={
                isGoingLive || isStartingWebMeeting
                  ? "starting"
                  : isStoppingLive || isStoppingWebMeeting
                  ? "stopping"
                  : effectiveAgenda?.live
                  ? "live"
                  : "stopped"
              }
              disabled={agendaButtonDisabled}
              isLoading={
                isGoingLive ||
                isStartingWebMeeting ||
                isStoppingLive ||
                isStoppingWebMeeting
              }
              onClick={handleAgendaActionButtonClick}
              isPastAgenda={!!effectiveAgenda?.endTime}
              {...(agendaButtonDisabledText && {
                tooltip: agendaButtonDisabledText,
              })}
              data-testid={
                effectiveAgenda?.live
                  ? "agenda-stop-button"
                  : "agenda-start-button"
              }
            />
          </HStack>
        </Flex>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          my="2"
          flexShrink={0}
        >
          {/* <Image src={liveEngSvg} /> */}
          <EPulse
            agenda={effectiveAgenda}
            isMinimized={isEPulseMinimized}
            onMinimizeChange={setIsEPulseMinimized}
          />
          {!isEPulseMinimized && (
            <Text
              fontSize="xs"
              textAlign="left"
              alignSelf="flex-start"
              mt={2}
              display={effectiveAgenda?.startTime ? "none" : "block"}
            >
              See audience engagement in real-time!
            </Text>
          )}
        </Box>

        {/* Main Content */}
        <Box flex="1" minH="0" display="flex" flexDirection="column">
          <VStack spacing={2} align="stretch" flexShrink={0}>
            {/* Activity Schedule Header */}
            <Flex justify="space-between" align="center">
              <Flex align="center" gap="4px">
                <RocketIcon />
                <Text fontSize="16px" color="dark.500">
                  Activity Schedule{" "}
                  {unrunActivitiesCount > 0 && `[${unrunActivitiesCount}]`}
                </Text>
              </Flex>
              <HStack spacing={2}>
                {/* Blueprint Button */}
                {effectiveAgenda?.blueprint && showBlueprintIcon && (
                  <IconButton
                    icon={<FiMap size={20} />}
                    size="sm"
                    variant="outline"
                    bg="white"
                    borderColor="rgba(208, 204, 213, 0.3)"
                    borderWidth="1px"
                    borderRadius="100px"
                    p={3}
                    color="#0C001C"
                    aria-label="View agenda blueprint"
                    onClick={onBlueprintModalOpen}
                    w="44px"
                    h="44px"
                    _hover={{
                      bg: "white",
                      borderColor: "rgba(208, 204, 213, 0.5)",
                    }}
                    _active={{
                      bg: "white",
                      borderColor: "rgba(208, 204, 213, 0.7)",
                    }}
                  />
                )}
                {/* Share Link Button - Only show for web users with join code */}
                {isWebUser(profile) && effectiveAgenda?.joinCode && (
                  <ShareLinkButton onClick={onShareModalOpen} />
                )}
                {/* Invite to App Button - Only show for Zoom App users */}
                {isZoomApp && (
                  <IconButton
                    icon={<FiSend size={20} />}
                    size="sm"
                    variant="outline"
                    bg="white"
                    borderColor="rgba(208, 204, 213, 0.3)"
                    borderWidth="1px"
                    borderRadius="100px"
                    p={3}
                    color="#0C001C"
                    aria-label="Invite participants to app"
                    onClick={handleInviteClick}
                    w="44px"
                    h="44px"
                    _hover={{
                      bg: "white",
                      borderColor: "rgba(208, 204, 213, 0.5)",
                    }}
                    _active={{
                      bg: "white",
                      borderColor: "rgba(208, 204, 213, 0.7)",
                    }}
                    data-testid="invite-to-app-button"
                  />
                )}
                {/* Add Activity Button */}
                <IconButton
                  icon={<FiPlus size={20} />}
                  size="sm"
                  variant="outline"
                  bg="white"
                  borderColor="rgba(208, 204, 213, 0.3)"
                  borderWidth="1px"
                  borderRadius="100px"
                  p={3}
                  color="#0C001C"
                  aria-label="Add activity"
                  onClick={onActivityModalOpen}
                  w="44px"
                  h="44px"
                  _hover={{
                    bg: "white",
                    borderColor: "rgba(208, 204, 213, 0.5)",
                  }}
                  _active={{
                    bg: "white",
                    borderColor: "rgba(208, 204, 213, 0.7)",
                  }}
                  data-testid="add-activity-button"
                />
              </HStack>
            </Flex>
          </VStack>

          {/* Activity Completion Notification */}
          {activityInGracePeriod && (
            <Box mt={2}>
              <ActivityCompletionNotification
                activityName={
                  activityInGracePeriod.displayName ||
                  activityInGracePeriod.title
                }
                activityType={activityInGracePeriod.type}
                hasLink={!!activityInGracePeriod.link}
                isVisible={true}
                onMoveNow={() => handleMoveNow(activityInGracePeriod._id)}
                onDismiss={() =>
                  handleDismissNotification(activityInGracePeriod._id)
                }
              />
            </Box>
          )}

          {/* Activity Cards - Now takes remaining space */}
          <Box flex="1" minH="0" mt="2">
            <ActivityList
              agendaId={agendaId}
              currentAgenda={effectiveAgenda}
              isLoading={fetchingAgenda || isAgendaLoading}
              onEditActivity={handleEditActivity}
            />
          </Box>
        </Box>
      </Box>

      {/* Floating End Demo button */}
      {isDemoActive && (
        <Box position="fixed" bottom="16px" right="16px" zIndex={1400}>
          <Button
            size="sm"
            variant="outline"
            fontSize="xs"
            color="gray.500"
            borderColor="gray.300"
            borderRadius="8px"
            bg="white"
            boxShadow="sm"
            _hover={{ bg: "gray.50", borderColor: "gray.400" }}
            onClick={handleEndDemo}
          >
            End Demo
          </Button>
        </Box>
      )}
    </>
  );
};

export default NewAgendaView;
